// Plural message entries — selected by Intl.PluralRules per locale. Replaces the
// hand-rolled `n === 1 ? "" : "s"` ternaries. Each form receives {count}.

import type { PluralSection } from "./types.js";

export const plurals: PluralSection = {
  // The import page: Google deletes a meeting's record 30 days after it ends.
  "plural.meetingDaysLeft": {
    en: { one: "{count} day left", other: "{count} days left" },
    de: { one: "Noch {count} Tag", other: "Noch {count} Tage" },
  },
  // A meeting's row on `/focus/meetings`: "2 Tickets · 1 Epic · 9 nicht
  // übernommen".
  "plural.meetingTickets": {
    en: { one: "{count} ticket", other: "{count} tickets" },
    de: { one: "{count} Ticket", other: "{count} Tickets" },
  },
  "plural.meetingEpics": {
    en: { one: "{count} epic", other: "{count} epics" },
    de: { one: "{count} Epic", other: "{count} Epics" },
  },
  // An issue row's open questions (refine light): the chip, and the link
  // page's top line, which carries its verb ("3 Fragen sind noch offen.").
  "plural.meetingQuestionsOpen": {
    en: { one: "{count} question open", other: "{count} questions open" },
    de: { one: "{count} Frage offen", other: "{count} Fragen offen" },
  },
  // The doubt card: the questions waiting behind its cap of five, from a
  // server that sent only the first five.
  "plural.meetingDoubtsPastCap": {
    en: {
      one: "{count} more question waits under “Not taken”.",
      other: "{count} more questions wait under “Not taken”.",
    },
    de: {
      one: "{count} weitere Rückfrage wartet unter „Nicht übernommen“.",
      other: "{count} weitere Rückfragen warten unter „Nicht übernommen“.",
    },
  },
  // The questions past the card's five, folded until asked for (every one
  // is answerable).
  "plural.meetingDoubtsMore": {
    en: { one: "{count} more question", other: "{count} more questions" },
    de: { one: "{count} weitere Rückfrage anzeigen", other: "{count} weitere Rückfragen anzeigen" },
  },
  // "Ticket aktualisieren": values cut because another of the meeting's issues holds them.
  "plural.meetingScopedOut": {
    en: { one: "Belongs to #{iid} ({count} point)", other: "Belongs to #{iid} ({count} points)" },
    de: { one: "Gehört zu #{iid} ({count} Punkt)", other: "Gehört zu #{iid} ({count} Punkte)" },
  },
  // A meeting's page and its list row: the items a question still holds.
  "plural.meetingWaiting": {
    en: { one: "{count} item waits for your answer", other: "{count} items wait for your answer" },
    de: { one: "{count} Punkt wartet auf deine Antwort", other: "{count} Punkte warten auf deine Antwort" },
  },
  "plural.meetingQuestionsOpenLine": {
    en: { one: "{count} question is still open.", other: "{count} questions are still open." },
    de: { one: "{count} Frage ist noch offen.", other: "{count} Fragen sind noch offen." },
  },
  "plural.meetingNotTaken": {
    en: { one: "{count} not taken", other: "{count} not taken" },
    de: { one: "{count} nicht übernommen", other: "{count} nicht übernommen" },
  },
  "plural.gaps": {
    en: { one: "{count} gap", other: "{count} gaps" },
    de: { one: "{count} Lücke", other: "{count} Lücken" },
  },
  "plural.questions": {
    en: { one: "{count} question", other: "{count} questions" },
    de: { one: "{count} Frage", other: "{count} Fragen" },
  },
  // Carries the VERB, not just the noun: German puts the number-agreeing verb
  // before the noun ("wartet noch 1 Frage" / "warten noch 3 Fragen"), so a
  // sentence that interpolated a bare "{count} Frage" would be ungrammatical at
  // count 1 — the issue page's pin read "Danach warten noch 1 Frage." (#248).
  "plural.askQueue": {
    en: {
      one: "{count} question still waiting after this one.",
      other: "{count} questions still waiting after this one.",
    },
    de: {
      one: "Danach wartet noch {count} Frage.",
      other: "Danach warten noch {count} Fragen.",
    },
  },
  // The issue card's held-answer bar. The answers go out TOGETHER once the user
  // is done — so the agent is not restarted once per click.
  "plural.heldAnswers": {
    en: {
      one: "{count} answer ready — Loom gets it once you’re done",
      other: "{count} answers ready — Loom gets them together once you’re done",
    },
    de: {
      one: "{count} Antwort bereit — Loom bekommt sie, sobald du fertig bist",
      other: "{count} Antworten bereit — Loom bekommt sie gesammelt, sobald du fertig bist",
    },
  },
  "plural.commits": {
    en: { one: "{count} commit", other: "{count} commits" },
    de: { one: "{count} Commit", other: "{count} Commits" },
  },
  "plural.children": {
    en: { one: "{count} child", other: "{count} children" },
    de: { one: "{count} Unterelement", other: "{count} Unterelemente" },
  },
  // The issue picker's meta line prints an issue's story points, and 1 is a
  // real value on `STORY_POINT_SCALE` — "1 Punkte" is ungrammatical.
  "plural.points": {
    en: { one: "{count} point", other: "{count} points" },
    de: { one: "{count} Punkt", other: "{count} Punkte" },
  },
  // The Figma sketch's COUNTED claim (focus/figma/FigmaPreview.tsx). Instead of
  // promising a fixed level of quality — which would be a lie on one of any two
  // real files — the caption counts this design's own compromises, and each
  // line renders only when its count is above zero. On a well-made design the
  // first reads "6 shapes"; on a file of `Rectangle 47` it reads "42", and the
  // reader calibrates their own trust. German needs the verb to agree too
  // ("ist gezeichnet" / "sind gezeichnet"), so the whole sentence is a form.
  //
  // The REASON is the counted one, and it has to stay that way: `plainBoxes`
  // counts a vector shape Loom could not draw from its outline, whatever
  // stopped it — the row holds none (read without outlines), or holds one Loom
  // refuses (over the size cap, past the drawing's outline budget, outside the
  // path alphabet, placed by a transform that does not reproduce Figma's own
  // box, or under a turned container whose turn Loom could not measure) — and
  // a shape whose gradient stops could not be read. "Holds no outline, or it
  // was too big to keep" named two of those and was false for the rest, so the
  // sentence says what is true of every one: Loom could not draw the real
  // outline or colours.
  "plural.figmaPlainBoxes": {
    en: {
      one: "{count} shape is drawn as a plain box — Loom couldn't draw its real outline or colours.",
      other: "{count} shapes are drawn as plain boxes — Loom couldn't draw their real outlines or colours.",
    },
    de: {
      one: "{count} Form ist als einfacher Kasten gezeichnet – Loom konnte ihre echte Kontur oder Farbe nicht zeichnen.",
      other: "{count} Formen sind als einfache Kästen gezeichnet – Loom konnte ihre echten Konturen oder Farben nicht zeichnen.",
    },
  },
  "plural.figmaHiddenOmitted": {
    en: {
      one: "{count} layer is switched off in the file and isn't drawn.",
      other: "{count} layers are switched off in the file and aren't drawn.",
    },
    de: {
      one: "{count} Ebene ist in der Datei ausgeschaltet und wird nicht gezeichnet.",
      other: "{count} Ebenen sind in der Datei ausgeschaltet und werden nicht gezeichnet.",
    },
  },
  "plural.figmaImagesOmitted": {
    en: {
      one: "{count} picture isn't stored and shows as an empty box.",
      other: "{count} pictures aren't stored and show as empty boxes.",
    },
    de: {
      one: "{count} Bild ist nicht gespeichert und erscheint als leerer Kasten.",
      other: "{count} Bilder sind nicht gespeichert und erscheinen als leere Kästen.",
    },
  },
};
